import { useId } from "react";
import { X } from "lucide-react";
import { cn } from "../../lib/utils.js";
import { EYEBROW } from "../../lib/typography.js";
import { ModalShell } from "../../primitives/index.js";

/* ---------------- Modal ----------------
   The public, opinionated dialog (increment 5.5) — first NEW component built
   on the proven engine. ModalShell supplies portal, scrim, focus trap with
   return-focus, Esc/outside dismissal, scroll lock, and inert background;
   this file is only skin: title bar + X, optional description, footer slot,
   three widths. For fully custom dialog chrome, compose ModalShell-level
   behavior via an organism instead of fighting these defaults. */

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  /** One eyebrow line above the title — a widget's name and type. Rendered in
   *  the shared EYEBROW style; not part of the dialog's accessible name. */
  context?: string;
  /** Muted helper line under the title. */
  description?: React.ReactNode;
  /** Optional id of body copy that describes the dialog. */
  describedBy?: string | undefined;
  /** Trailing action group in the header, placed just before Close. */
  headerActionsSlot?: React.ReactNode;
  /** Right-aligned action row (Buttons). */
  footer?: React.ReactNode;
  /** sm 400px · md 560px (default) · lg 760px — all clamp to 96vw. `editor`
   *  is a centred 1200px editor with a stable, viewport-clamped height. `full` is
   *  the overlay-only special size: 96vw × 94dvh (max 1600px), always
   *  centred whatever `placement` says. */
  size?: "sm" | "md" | "lg" | "editor" | "full";
  /** Center complex editors; retain the familiar upper placement by default. */
  placement?: "top" | "center";
  initialFocusRef?: React.RefObject<HTMLElement | null>;
  /** Layout-only panel sizing for editors that embed a preview beside a form. */
  className?: string;
  children?: React.ReactNode;
}

const SIZE: Record<"sm" | "md" | "lg" | "editor", string> = {
  sm: "w-100",
  md: "w-140",
  lg: "w-190",
  editor: "w-300 h-[min(52rem,calc(100dvh-3rem))]",
};

export function Modal({
  open,
  onClose,
  title,
  context,
  description,
  describedBy,
  headerActionsSlot,
  footer,
  size = "md",
  placement = "top",
  initialFocusRef,
  className,
  children,
}: ModalProps) {
  const titleId = useId();
  const isFull = size === "full";
  const centered = isFull || size === "editor" || placement === "center";

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      labelledBy={titleId}
      describedBy={describedBy}
      initialFocusRef={initialFocusRef}
      // The top inset scales with viewport height, so browser zoom cannot
      // spend the whole available height on the normal 6rem placement.
      {...(!centered ? { panelStyle: { marginTop: "min(6rem, 10dvh)" } } : {})}
      containerClassName={cn(
        centered
          ? "items-center justify-center p-4 sm:p-6"
          : "items-start justify-center p-4 sm:p-6",
      )}
      panelClassName={cn(
        isFull
          ? "w-[96vw] h-[94dvh] max-w-400 max-h-[calc(100dvh-2rem)] sm:max-h-[calc(100dvh-3rem)]"
          : centered
            ? "max-w-[calc(100vw-2rem)] max-h-[calc(100dvh-2rem)] sm:max-h-[calc(100dvh-3rem)]"
            : "max-w-[calc(100vw-2rem)] max-h-[calc(100dvh-2rem-min(6rem,10dvh))] sm:max-h-[calc(100dvh-3rem-min(6rem,10dvh))]",
        "bg-pb-card-elevated border border-pb-border-strong",
        "rounded-xl shadow-2xl pb-hairline flex flex-col overflow-y-auto",
        !isFull && SIZE[size],
        className,
      )}
    >
      <div
        className={cn(
          "shrink-0",
          "flex items-start justify-between gap-3 px-4 py-3 border-b border-pb-border",
        )}
      >
        <div className="min-w-0">
          {context && <p className={cn("mb-0.5", EYEBROW)}>{context}</p>}
          <div
            id={titleId}
            className="text-sm font-medium text-pb-text leading-snug"
          >
            {title}
          </div>
          {description && (
            <p className="mt-0.5 text-dense-1 text-pb-text-muted">
              {description}
            </p>
          )}
        </div>
        {/* Without header actions, Close is today's direct child of the header. */}
        {headerActionsSlot ? (
          <div className="shrink-0 flex items-center gap-1">
            {headerActionsSlot}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="-mt-0.5 -me-1 w-7 h-7 flex items-center justify-center rounded text-pb-text-muted hover:text-pb-text hover-quiet press focus-ring"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 -mt-0.5 -me-1 w-7 h-7 flex items-center justify-center rounded text-pb-text-muted hover:text-pb-text hover-quiet press focus-ring"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {/* Only when there IS a body. Rendered unconditionally this was a p-4
          empty strip between the header's border-b and the footer's border-t,
          so every confirm dialog — which says everything in `description` and
          passes no children — showed a blank band that read as a stray rule
          across the middle of the box. */}
      {children != null && children !== false ? (
        <div className="flex-1 min-h-0 overflow-y-auto [@media(max-height:30rem)]:shrink-0">
          <div className={cn("p-4", (size === "editor" || isFull) && "h-full")}>
            {children}
          </div>
        </div>
      ) : null}
      {footer && (
        <div
          className={cn(
            "shrink-0 flex flex-wrap items-center justify-end gap-2 px-4 py-3 border-t border-pb-border bg-pb-shell/40",
          )}
        >
          {footer}
        </div>
      )}
    </ModalShell>
  );
}
